import { create } from 'zustand';
import type { Execution } from '../schemas';
import { mockExecutions } from '../data/mockExecutions';

interface ExecutionState {
  executions: Execution[];
  isLoading: boolean;
  addExecution: (execution: Omit<Execution, 'id'>) => void;
  updateExecution: (id: string, updates: Partial<Execution>) => void;
  retryExecution: (id: string) => void;
  downloadResult: (execution: Execution) => void;
}

export const useExecutionStore = create<ExecutionState>()((set, get) => ({
  executions: mockExecutions,
  isLoading: false,
  addExecution: execution => {
    const newExecution: Execution = {
      ...execution,
      id: Math.random().toString(36).substr(2, 9),
    };
    set(state => ({ executions: [newExecution, ...state.executions] }));
  },
  updateExecution: (id, updates) => {
    set(state => ({
      executions: state.executions.map(e => (e.id === id ? { ...e, ...updates } : e)),
    }));
  },
  retryExecution: id => {
    const execution = get().executions.find(e => e.id === id);
    if (execution) {
      // Create a new execution for the retry
      const retryExecution: Execution = {
        ...execution,
        id: Math.random().toString(36).substr(2, 9),
        status: 'pending',
        startedAt: new Date().toISOString(),
        completedAt: undefined,
        errorMessage: undefined,
        resultFileUrl: undefined,
        recordsProcessed: undefined,
      };

      set(state => ({ executions: [retryExecution, ...state.executions] }));

      // Simulate execution progress
      setTimeout(() => {
        get().updateExecution(retryExecution.id, { status: 'running' });

        setTimeout(() => {
          // Randomly succeed or fail the retry
          const success = Math.random() > 0.3; // 70% success rate for retries
          if (success) {
            get().updateExecution(retryExecution.id, {
              status: 'completed',
              completedAt: new Date().toISOString(),
              recordsProcessed: Math.floor(Math.random() * 10000) + 100,
              resultFileUrl: `/downloads/retry_${retryExecution.id}_csv`,
            });
          } else {
            get().updateExecution(retryExecution.id, {
              status: 'failed',
              completedAt: new Date().toISOString(),
              errorMessage: 'Retry failed: Same underlying issue persists',
            });
          }
        }, 3000);
      }, 1000);
    }
  },
  downloadResult: execution => {
    if (execution.resultFileUrl) {
      // In a real app, this would trigger an actual file download
      const link = document.createElement('a');
      link.href = execution.resultFileUrl;
      link.download = `execution_${execution.id}_result.${execution.resultFileUrl.split('_').pop()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show a success message
      alert(`Download started for execution ${execution.id} result file.`);
    }
  },
}));
